import { Fragment, useEffect } from "react";
import {
  Box,
  Button,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { Command } from "~/components/guides/components";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  ActionType,
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  IntegrationType,
  useCreateClientIntegrationMutation,
  useTriggerActionLazyQuery,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { getError } from "~/lib/handle-error";
import {
  helperTextStyles,
  ValidationMessage,
} from "../../utils/integrationValidationUtils";
import { IntegrationAddHeader } from "../../headers/IntegrationAddHeader";
import useGenerateIntegrationName from "../../utils/integrationsUtils";
import { UpdateFlowData } from "../../utils/integrationsTypes";
import { IntegrationToggleOption } from "~/pages/integrations/components/toggle-option/IntegrationToggleOption";
import { motion } from "motion/react";
import { Flex } from "~/components/Flex";
import { SubField } from "~/components/Form/components";
import { PasswordField } from "~/components/Form/components/PasswordField";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

type CloudFormInput = {
  integrationName: string;
  clientId: string;
  clientSecret: string;
  cloud: string;
  createAssets: boolean;
  mondooEnrichment: boolean;
  useMemberCID: boolean;
  memberCID: string;
};

const defaultValues: CloudFormInput = {
  integrationName: "",
  clientId: "",
  clientSecret: "",
  cloud: "",
  createAssets: false,
  mondooEnrichment: true,
  useMemberCID: false,
  memberCID: "",
};

const CLIENT_SECRET_BLOCK = "**********************";
const clientSecretBlockRegex = /^\*+$/;

export function CrowdStrikeIntegrationForm({
  scope,
  updateFlow,
}: {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}) {
  let navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const defaultIntegrationName = useGenerateIntegrationName({ scope });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    getFieldState,
    formState: { errors, isValid, isSubmitSuccessful },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const [clientSecret, useMemberCID] = watch(["clientSecret", "useMemberCID"]);

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [triggerAction] = useTriggerActionLazyQuery();

  const triggerClientIntegrationScan = async (mrn: string) => {
    try {
      await triggerAction({
        variables: { input: { mrn, type: ActionType.RunImport } },
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (updateFlow) {
      if (
        updateFlow?.integration.configurationOptions?.__typename !==
        "CrowdstrikeFalconConfigurationOptions"
      )
        return;

      reset({
        integrationName: updateFlow.integration.name,
        clientId: updateFlow.integration.configurationOptions.clientId,
        cloud: updateFlow.integration.configurationOptions?.cloud || "",
        clientSecret: CLIENT_SECRET_BLOCK,
        createAssets: Boolean(
          updateFlow.integration.configurationOptions?.createAssets,
        ),
        mondooEnrichment:
          updateFlow.integration.configurationOptions?.mondooEnrichment ?? true,
        useMemberCID: Boolean(
          updateFlow.integration.configurationOptions?.memberCID,
        ),
        memberCID: updateFlow.integration.configurationOptions?.memberCID ?? "",
      });
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  const onSubmit: SubmitHandler<CloudFormInput> = async (data) => {
    const {
      clientId,
      clientSecret,
      cloud,
      createAssets,
      mondooEnrichment,
      useMemberCID,
      memberCID,
    } = data;
    if (!updateFlow) {
      if (!clientSecret) {
        throw new Error("You must supply a valid client secret");
      }
    }

    const crowdstrikeFalconConfigurationOptions = {
      clientId,
      ...(clientSecret && !clientSecretBlockRegex.test(clientSecret)
        ? { clientSecret }
        : { clientSecret: "" }),
      cloud,
      createAssets,
      mondooEnrichment,
      ...(useMemberCID ? { memberCID } : {}),
    };

    try {
      if (updateFlow) {
        const integrationId = updateFlow.integration.mrn.split("/").pop();
        const mrn = getIntegrationMrn({ scope, integrationId });
        await updateIntegration({
          variables: {
            input: {
              mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.CrowdstrikeFalcon,
              configurationOptions: {
                crowdstrikeFalconConfigurationOptions,
              },
            },
          },
        });

        // kick off the import
        await triggerClientIntegrationScan(mrn);

        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });

        navigate(
          `/space/integrations/crowdstrike/${integrationId}?${scope.params}`,
        );
      } else {
        const newIntegration = await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.CrowdstrikeFalcon,
              longLivedToken: false,
              configurationOptions: {
                crowdstrikeFalconConfigurationOptions,
              },
            },
          },
        });

        const mrn =
          newIntegration.data?.createClientIntegration.integration.mrn;
        const integrationId = mrn?.split("/").pop();

        if (!mrn) {
          throw new Error("Failed to create integration");
        }

        // kick off the import
        await triggerClientIntegrationScan(mrn);

        enqueueSnackbar("Successfully created integration", {
          variant: "success",
        });

        navigate(
          `/space/integrations/crowdstrike/${integrationId}?${scope.params}`,
        );
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const preferencesForm = (
    // Step 7
    <Box pb={4}>
      <Command
        number={7}
        options={{
          fontSize: { xs: 16 },
          dotColor: theme.palette.background.lightest,
        }}
      >
        Configure preferences
      </Command>
      <Box sx={{ mt: 3, pl: 6 }}>
        <IntegrationToggleOption
          title="Create new assets for unique detections"
          description="When a third-party data integration discovers an asset that doesn't match any existing assets in this space, add the new asset."
          formOptions={{ control, name: "createAssets" }}
        />
        <IntegrationToggleOption
          title="Enrich data with Mondoo vulnerability detection"
          description="Use Mondoo's vulnerability database to enrich imported third-party data with additional package and end-of-life advisories."
          formOptions={{ control, name: "mondooEnrichment" }}
        />
      </Box>
    </Box>
  );

  return (
    <Fragment>
      <IntegrationAddHeader {...{ type: IntegrationType.CrowdstrikeFalcon }} />
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Step 1 */}
          <Box pb={4}>
            <Command
              number={1}
              options={{
                fontSize: { xs: 16 },
                dotColor: theme.palette.background.lightest,
              }}
            >
              Create a new API client in CrowdStrike Falcon
            </Command>
            <Box pl={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 2 }}
              >
                To integrate Mondoo and CrowdStrike, go to the CrowdStrike
                Falcon console and create a new API client for Mondoo. Provide
                the API client's client ID, secret, and base URL below. To learn
                more, read the{" "}
                <Link
                  href="https://mondoo.com/docs/infra/imports/crowdstrike"
                  target="_blank"
                  rel="noopener"
                >
                  Mondoo documentation
                </Link>
                .
              </Typography>

              <Button
                href="https://falcon.crowdstrike.com/login/"
                target="_blank"
                variant="outlined"
                color="secondary"
                endIcon={<OpenInNewIcon />}
              >
                Go to CrowdStrike console
              </Button>
            </Box>
          </Box>
          {/* Step 2 */}
          <Box pb={4}>
            <Command
              number={2}
              options={{
                fontSize: { xs: 16 },
                dotColor: theme.palette.background.lightest,
              }}
            >
              Choose an integration name
            </Command>
            <Box pl={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 2 }}
              >
                Use a descriptive name that lets you easily identify the
                integration later.
              </Typography>
              <Controller
                name="integrationName"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    sx={{
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="Your integration name..."
                    error={Boolean(errors.integrationName)}
                    helperText={
                      Boolean(errors.integrationName) && (
                        <ValidationMessage error={errors.integrationName} />
                      )
                    }
                  />
                )}
              />
            </Box>
          </Box>

          {/* step 3 */}
          <Box pb={4}>
            <Command
              number={3}
              options={{
                fontSize: { xs: 16 },
                dotColor: theme.palette.background.lightest,
              }}
            >
              Provide the client ID
            </Command>
            <Box pl={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 2 }}
              >
                Paste the client ID of the CrowdStrike API client you created
                for Mondoo.
              </Typography>
              <Controller
                name="clientId"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    sx={{
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="e.g. A12B34567C"
                    error={
                      getFieldState("clientId").isTouched &&
                      Boolean(errors.clientId)
                    }
                    helperText={
                      Boolean(errors.clientId) &&
                      getFieldState("clientId").isTouched && (
                        <ValidationMessage error={errors.clientId} />
                      )
                    }
                  />
                )}
              />
            </Box>
          </Box>

          {/* Step 4 */}
          <Box pb={4}>
            <Command
              number={4}
              options={{
                fontSize: { xs: 16 },
                dotColor: theme.palette.background.lightest,
              }}
            >
              Provide the secret
            </Command>
            <Box pl={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 2 }}
              >
                Paste the secret of the CrowdStrike API client you created for
                Mondoo.
              </Typography>
              <Controller
                name="clientSecret"
                control={control}
                rules={{
                  required: updateFlow ? false : true,
                }}
                render={({ field }) => (
                  <PasswordField
                    {...field}
                    fullWidth
                    sx={{
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="e.g. 1234abcd5678efgh9012ijkl3456mnop"
                    error={
                      getFieldState("clientSecret").isTouched &&
                      Boolean(errors.clientSecret)
                    }
                    helperText={
                      Boolean(errors.clientSecret) &&
                      getFieldState("clientSecret").isTouched && (
                        <ValidationMessage error={errors.clientSecret} />
                      )
                    }
                  />
                )}
              />
            </Box>
          </Box>

          {/* step 5 */}
          <Box pb={4}>
            <Command
              number={5}
              options={{
                fontSize: { xs: 16 },
                dotColor: theme.palette.background.lightest,
              }}
            >
              Provide the Cloud URL
            </Command>
            <Box pl={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 2 }}
              >
                Paste the Cloud URL of the CrowdStrike API client you created
                for Mondoo.
              </Typography>
              <Controller
                name="cloud"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    sx={{
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="https://api.your-region.crowdstrike.com/"
                    error={
                      getFieldState("cloud").isTouched && Boolean(errors.cloud)
                    }
                    helperText={
                      Boolean(errors.cloud) &&
                      getFieldState("cloud").isTouched && (
                        <ValidationMessage error={errors.cloud} />
                      )
                    }
                  />
                )}
              />
            </Box>
          </Box>

          {/* Step 6 */}
          <Box pb={4}>
            <Box>
              <Command
                number={6}
                options={{
                  fontSize: { xs: 16 },
                  dotColor: theme.palette.background.lightest,
                }}
              >
                Configure member CID usage
              </Command>
              <Box sx={{ mt: 2, pl: 6 }}>
                <IntegrationToggleOption
                  title="Export data from a specific member CID"
                  description="If the API client you created for Mondoo has access to multiple CIDs, you can choose a single member CID from which to export data."
                  formOptions={{
                    name: "useMemberCID",
                    control,
                  }}
                />

                <Box
                  component={motion.div}
                  initial={false}
                  transition={{
                    duration: 0.35,
                    staggerChildren: 0.25,
                  }}
                  sx={{ overflow: "hidden", pt: 1 }}
                >
                  <SubField
                    configuration="Provide the member CID"
                    caption={
                      <Fragment>
                        To learn how to find the member CID, read the{" "}
                        <Link
                          href="https://mondoo.com/docs/infra/imports/crowdstrike"
                          target="_blank"
                          rel="noopener"
                        >
                          Mondoo documentation
                        </Link>
                      </Fragment>
                    }
                    disabled={!useMemberCID}
                    field={
                      <Flex flex="1 0 auto" alignItems="flex-end">
                        <Controller
                          name="memberCID"
                          control={control}
                          rules={{
                            required: false,
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              disabled={!useMemberCID}
                              sx={{
                                background: theme.palette.code.background,
                                borderRadius: 1,
                                color: "text.primary",
                                ...helperTextStyles,
                              }}
                              label="Member CID"
                              placeholder="e.g., 1234abcd5678efgh9012ijkl3456mnop"
                              slotProps={{
                                inputLabel: { shrink: true },
                              }}
                              error={
                                getFieldState("memberCID").isTouched &&
                                Boolean(errors.memberCID)
                              }
                              helperText={
                                Boolean(errors.memberCID) &&
                                getFieldState("memberCID").isTouched && (
                                  <ValidationMessage error={errors.memberCID} />
                                )
                              }
                            />
                          )}
                        />
                      </Flex>
                    }
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Step 7 */}
          {preferencesForm}

          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              endIcon={<ChevronRightIcon />}
              // setValue doesn't trigger the required validation so we're doing it manually here
              disabled={updateFlow ? !isValid : !isValid || !clientSecret}
            >
              {updateFlow ? "update configuration" : "create integration"}
            </Button>
          </Box>
        </form>
      </Box>
    </Fragment>
  );
}
